/**
 * Win-Back Promotion Templates
 * Re-engage churned or inactive users
 */

import { PromotionTemplate } from "./types";

export const WIN_BACK_TEMPLATES: PromotionTemplate[] = [
  {
    id: "we-miss-you",
    name: "We Miss You",
    category: "win_back",
    description: "Emotional re-engagement for churned subscribers",
    strategy: "Churned users already know your product. A heartfelt 'we miss you' with a strong discount often brings them back. Best sent 30-60 days after churn.",
    icon: "HeartOutlined",
    color: "#EF4444",
    expectedOutcomes: [
      "5-15% win-back rate",
      "Lower CAC than new acquisition",
      "Feedback on why they left",
    ],
    bestPractices: [
      "Wait 30+ days after churn",
      "Acknowledge their absence",
      "Offer meaningful discount (40%+)",
      "Highlight what's new since they left",
    ],
    tags: ["win-back", "churned", "re-engagement", "retention"],
    bestFor: ["Subscription products", "SaaS with churn", "Consumer apps"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 8,
      targetAudience: ["churned"],
      discount: {
        discountType: "percentage",
        discountValue: 50,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "default",
        primaryColor: "#EF4444",
        secondaryColor: "#F97316",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["all"],
        maxImpressionsPerUser: 5,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "We Miss You!",
          subheadline: "It's been a while. Come back with 50% off your first month.",
          ctaText: "Come Back",
          ctaSecondaryText: "What's New",
          badge: "WELCOME BACK",
          terms: "*Offer for returning subscribers. Valid for 7 days.",
        },
      },
    },
  },
  {
    id: "reactivation-offer",
    name: "Reactivation Offer",
    category: "win_back",
    description: "Direct reactivation offer for recently churned users",
    strategy: "Strike while the iron is warm. Users who churned recently still remember your product. A quick follow-up offer within 7-14 days can prevent permanent churn.",
    icon: "ReloadOutlined",
    color: "#3B82F6",
    expectedOutcomes: [
      "Higher win-back rate (10-20%)",
      "Immediate revenue recovery",
      "Understanding of churn reasons",
    ],
    bestPractices: [
      "Act within 14 days of churn",
      "Address common churn reasons",
      "Make re-subscribing frictionless",
      "Offer to help with onboarding",
    ],
    tags: ["win-back", "reactivation", "churned", "quick-follow-up"],
    bestFor: ["SaaS products", "Subscription services"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 9,
      targetAudience: ["churned"],
      discount: {
        discountType: "percentage",
        discountValue: 40,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_blue",
        primaryColor: "#3B82F6",
        secondaryColor: "#6366F1",
        animation: "slide",
      },
      displayConditions: {
        showOnPages: ["all"],
        maxImpressionsPerUser: 3,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Wait! Before You Go...",
          subheadline: "We'd love to have you back. Here's 40% off to reconsider.",
          ctaText: "Reactivate Now",
          ctaSecondaryText: "Tell Us Why You Left",
          badge: "SAVE 40%",
          terms: "*Offer expires in 48 hours.",
        },
      },
    },
  },
  {
    id: "inactive-user-nudge",
    name: "Inactive User Nudge",
    category: "win_back",
    description: "Re-engage users who haven't been active recently",
    strategy: "Users who stop using your product are at risk of churning. A gentle nudge with an incentive can bring them back before they officially cancel.",
    icon: "BellOutlined",
    color: "#8B5CF6",
    expectedOutcomes: [
      "Reduced churn rate",
      "Re-engagement of at-risk users",
      "Early warning on product issues",
    ],
    bestPractices: [
      "Trigger after 14-30 days of inactivity",
      "Highlight value they're missing",
      "Offer help or onboarding",
      "Don't be pushy",
    ],
    tags: ["win-back", "inactive", "at-risk", "prevention"],
    bestFor: ["Products with usage metrics", "Subscription services"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "banner",
      displayStyle: "minimal",
      priority: 6,
      targetAudience: ["paid", "free"],
      discount: {
        discountType: "percentage",
        discountValue: 20,
        currency: "USD",
        applicablePlans: ["pro"],
      },
      styling: {
        theme: "default",
        primaryColor: "#8B5CF6",
        secondaryColor: "#EC4899",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard"],
        maxImpressionsPerUser: 3,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "We've Missed You!",
          subheadline: "It's been a while since you've used Copilotly. Need help getting started again?",
          ctaText: "Show Me What's New",
          ctaSecondaryText: "Get Help",
          badge: "COME BACK",
          terms: "",
        },
      },
    },
  },
  {
    id: "downgrade-prevention",
    name: "Downgrade Prevention",
    category: "win_back",
    description: "Offer to retain users attempting to downgrade",
    strategy: "When users try to downgrade, offer a discount to keep them on their current plan. They've already shown intent to stay, just at a lower price point.",
    icon: "WarningOutlined",
    color: "#F59E0B",
    expectedOutcomes: [
      "Reduced revenue churn",
      "Maintained plan levels",
      "Understanding of price sensitivity",
    ],
    bestPractices: [
      "Trigger on downgrade page visit",
      "Offer discount on current plan",
      "Highlight features they'll lose",
      "Make staying easy",
    ],
    tags: ["win-back", "downgrade", "prevention", "retention"],
    bestFor: ["Tiered SaaS products", "Products with plan levels"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 9,
      targetAudience: ["paid"],
      discount: {
        discountType: "percentage",
        discountValue: 30,
        currency: "USD",
        applicablePlans: ["pro", "enterprise"],
      },
      styling: {
        theme: "default",
        primaryColor: "#F59E0B",
        secondaryColor: "#EF4444",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["billing"],
        maxImpressionsPerUser: 2,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Wait! Keep Your Pro Features",
          subheadline: "We'd hate to see you lose access. Stay on Pro with 30% off.",
          ctaText: "Keep Pro at 30% Off",
          ctaSecondaryText: "Continue Downgrade",
          badge: "SAVE 30%",
          terms: "*Discount applies to next 3 months.",
        },
      },
    },
  },
];
